import React, { useEffect, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import Modal from '../../components/common/Modal';
import Toast from '../../components/common/Toast';
import engineerService from '../../services/engineerService';
import { useAuth } from '../../hooks/useAuth';
import { useSite } from '../../contexts/SiteContext';
import { useToast } from '../../hooks/useToast';
import { formatDate } from '../../utils/dateFormatter';
import { ROLES } from '../../constants/roles';

const EngineersView = () => {
  const { user } = useAuth();
  const { selectedSite } = useSite();
  const [engineers, setEngineers] = useState([]);
  const [filteredEngineers, setFilteredEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ start_date: '', end_date: '', reason: '' });
  const [leaveErrors, setLeaveErrors] = useState({});

  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    if (user?.id) {
      loadEngineers();
    }
  }, [user?.id, selectedSite]);

  useEffect(() => {
    filterEngineers();
  }, [searchTerm, statusFilter, engineers, selectedSite]);

  const loadEngineers = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    const teamLeadId = user?.role === ROLES.TL ? user.id : null;
    const result = await engineerService.getAllEngineers(teamLeadId, selectedSite);
    if (result.success) {
      setEngineers(result.data || []);
      setFilteredEngineers(result.data || []);
    } else {
      error(result.error || 'Failed to load engineers');
    }

    if (!silent) {
      setLoading(false);
    }
  };

  const resetLeaveForm = () => {
    setLeaveForm({ start_date: '', end_date: '', reason: '' });
    setLeaveErrors({});
  };

  const loadEngineerLeaves = async (engineerId) => {
    setLoadingLeaves(true);
    const leavesResult = await engineerService.getEngineerLeaves(engineerId);
    if (leavesResult.success) {
      setLeaveRecords(Array.isArray(leavesResult.data) ? leavesResult.data : []);
    } else {
      error(leavesResult.error || 'Failed to load leave records');
      setLeaveRecords([]);
    }
    setLoadingLeaves(false);
  };

  const handleOpenLeaveModal = async (engineer) => {
    setSelectedEngineer(engineer);
    setShowLeaveModal(true);
    resetLeaveForm();
    await loadEngineerLeaves(engineer.id);
  };

  const handleCloseLeaveModal = () => {
    if (submittingLeave) return;
    setShowLeaveModal(false);
    setSelectedEngineer(null);
    setLeaveRecords([]);
    resetLeaveForm();
  };

  const handleLeaveInputChange = (e) => {
    const { name, value } = e.target;
    setLeaveForm((prev) => ({ ...prev, [name]: value }));
    if (leaveErrors[name]) {
      setLeaveErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateLeaveForm = () => {
    const nextErrors = {};
    if (!leaveForm.start_date) {
      nextErrors.start_date = 'Start date is required';
    }
    if (!leaveForm.end_date) {
      nextErrors.end_date = 'End date is required';
    }
    if (leaveForm.start_date && leaveForm.end_date && leaveForm.end_date < leaveForm.start_date) {
      nextErrors.end_date = 'End date cannot be before start date';
    }

    setLeaveErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitLeave = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (!selectedEngineer || !validateLeaveForm()) return;

    setSubmittingLeave(true);
    const payload = {
      start_date: leaveForm.start_date,
      end_date: leaveForm.end_date,
      reason: leaveForm.reason.trim()
    };

    const leaveResult = await engineerService.markEngineerOnLeave(selectedEngineer.id, payload);
    if (leaveResult.success) {
      success(`Leave added for ${selectedEngineer.name}`);
      await Promise.all([loadEngineerLeaves(selectedEngineer.id), loadEngineers({ silent: true })]);
      resetLeaveForm();
    } else {
      error(leaveResult.error || 'Failed to mark engineer on leave');
    }
    setSubmittingLeave(false);
  };

  const handleRevokeLeave = async (leaveId) => {
    if (!selectedEngineer) return;

    setSubmittingLeave(true);
    const revokeResult = await engineerService.revokeEngineerLeave(selectedEngineer.id, leaveId);
    if (revokeResult.success) {
      success('Leave revoked successfully');
      await Promise.all([loadEngineerLeaves(selectedEngineer.id), loadEngineers({ silent: true })]);
    } else {
      error(revokeResult.error || 'Failed to revoke leave');
    }
    setSubmittingLeave(false);
  };

  const filterEngineers = () => {
    // Exclude TL in Engineers page TL should appear only in assign ticket drop down list
    let filtered = engineers.filter(eng => eng.id !== user?.id)

    if (searchTerm) {
      filtered = filtered.filter(
        (engineer) =>
          engineer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          engineer.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((engineer) => engineer.status === statusFilter);
    }

    setFilteredEngineers(filtered);
  };

  const getEngineerStats = () => {
    const total = filteredEngineers.length;
    const active = filteredEngineers.filter((e) => e.status === 'Active').length;
    const busy = filteredEngineers.filter((e) => e.status === 'Busy').length;
    const avgPerformance = filteredEngineers.reduce((sum, e) => sum + (e.performance_score || 0), 0) / total || 0;

    const engineersWithTime = filteredEngineers.filter((e) => (e.avg_response_time || 0) > 0);
    const avgResponseTime = engineersWithTime.reduce((sum, e) => sum + (e.avg_response_time || 0), 0) / engineersWithTime.length || 0;

    return { 
      total, 
      active, 
      busy, 
      avgPerformance: avgPerformance.toFixed(1),
      avgResponseTime: avgResponseTime.toFixed(1)
    };
  };

  const stats = getEngineerStats();

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  };

  const filterGroupStyle = {
    flex: '1',
    minWidth: '200px'
  };

  const selectStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--surface)',
    color: 'var(--text-primary)',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return '#10b981';
      case 'Busy':
        return '#f59e0b';
      case 'On Leave':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const getLeaveBadge = (leave) => {
    if (leave.revoked_at) {
      return { label: 'Revoked', background: '#fee2e2', color: '#b91c1c' };
    }

    const now = new Date();
    const start = new Date(leave.start_date);
    const end = new Date(leave.end_date);

    if (now >= start && now <= end) {
      return { label: 'Active', background: '#dcfce7', color: '#166534' };
    }

    if (now < start) {
      return { label: 'Upcoming', background: '#dbeafe', color: '#1e40af' };
    }

    return { label: 'Completed', background: '#f3f4f6', color: '#4b5563' };
  };

  const columns = [
    {
      key: 'name',
      label: 'Engineer',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'var(--primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '700',
              fontSize: '16px'
            }}
          >
            {value.charAt(0)}
          </div>
          <div>
            <div style={{ fontWeight: '600' }}>{value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{row.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact'
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span
          style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            background: `${getStatusColor(value)}20`,
            color: getStatusColor(value)
          }}
        >
          {value}
        </span>
      )
    },
    {
      key: 'team_lead',
      label: 'Team Lead',
      render: (value) => {
        if (!value) {
          return (
            <span
              style={{
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                fontSize: '13px'
              }}
            >
              Not Assigned
            </span>
          );
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{value.name}</span>
          </div>
        );
      }
    },
    {
      key: 'active_tickets',
      label: 'Active Tickets',
      render: (value) => (
        <span
          style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '700',
            background: 'var(--surface-light)',
            color: 'var(--text-primary)'
          }}
        >
          {value}
        </span>
      )
    },
    {
      key: 'completed_this_month',
      label: 'Completed',
      render: (value) => `${value} tickets`
    },
    {
      key: 'performance_score',
      label: 'Performance',
      render: (value) => {
        const color = value >= 90 ? '#10b981' : value >= 75 ? '#f59e0b' : '#ef4444';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                flex: 1,
                height: '8px',
                background: 'var(--surface-light)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  width: `${value}%`,
                  height: '100%',
                  background: color,
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
            <span style={{ fontSize: '12px', fontWeight: '700', color, minWidth: '35px' }}>{value}%</span>
          </div>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>
        Loading engineers...
      </div>
    );
  }

  return (
    <div>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      <div style={gridStyle}>
        <StatCard title="Total Engineers" value={stats.total} icon="" color="#3b82f6" 
        onClick={() => setStatusFilter('ALL')} />
        <StatCard title="Active Today" value={stats.active} icon="" subtitle="Available for assignment" color="#10b981"
        onClick={() => setStatusFilter('Active')} />
        <StatCard title="Busy" value={stats.busy} icon="" subtitle="Working on tickets" color="#f59e0b" 
        onClick={() => setStatusFilter('Busy')}/>
        <StatCard
          title="Avg Performance"
          value={`${stats.avgPerformance}%`}
          icon=""
          color="#f59e0b"
        />
        <StatCard
          title="Avg Response Time"
          value={`${stats.avgResponseTime} hrs`}
          icon=""
          color="#06b6d4"
        />
      </div>

      <div style={filterBarStyle}>
        <div style={filterGroupStyle}>
          <Input
            placeholder="Search engineers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            }
          />
        </div>
        <div style={filterGroupStyle}>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '6px'
            }}
          >
            Filter by Status
          </label>
          <select style={selectStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="Active">Active</option>
            <option value="Busy">Busy</option>
            <option value="On Leave">On Leave</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <Button variant="secondary" onClick={loadEngineers}>
            Refresh
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredEngineers}
        actions={[
          { label: 'Manage Leave', onClick: handleOpenLeaveModal }
        ]}
        pageSize={15}
      />

      <Modal
        isOpen={showLeaveModal}
        onClose={handleCloseLeaveModal}
        title={selectedEngineer ? `Manage Leave - ${selectedEngineer.name}` : 'Manage Leave'}
        size="large"
        footer={(
          <>
            <Button variant="secondary" onClick={handleCloseLeaveModal} disabled={submittingLeave}>
              Close
            </Button>
            <Button variant="primary" onClick={handleSubmitLeave} disabled={submittingLeave}>
              {submittingLeave ? 'Saving...' : 'Mark Leave'}
            </Button>
          </>
        )}
      >
        <form onSubmit={handleSubmitLeave}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
              marginBottom: '12px'
            }}
          >
            <Input
              label="Leave Start Date"
              type="date"
              name="start_date"
              value={leaveForm.start_date}
              onChange={handleLeaveInputChange}
              error={leaveErrors.start_date}
              required
            />
            <Input
              label="Leave End Date"
              type="date"
              name="end_date"
              value={leaveForm.end_date}
              onChange={handleLeaveInputChange}
              error={leaveErrors.end_date}
              required
            />
          </div>

          <Input
            label="Reason (optional)"
            name="reason"
            value={leaveForm.reason}
            onChange={handleLeaveInputChange}
            placeholder="Annual leave, medical leave, etc."
          />

          <div
            style={{
              marginTop: '20px',
              borderTop: '1px solid var(--border)',
              paddingTop: '16px'
            }}
          >
            <h4 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)', fontSize: '15px' }}>Existing Leave Records</h4>

            {loadingLeaves ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading leave records...</div>
            ) : leaveRecords.length === 0 ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>No leave records found for this engineer.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {leaveRecords.map((leave) => {
                  const badge = getLeaveBadge(leave);
                  return (
                    <div
                      key={leave.id}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: '10px',
                        padding: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                            {formatDate(leave.start_date)} to {formatDate(leave.end_date)}
                          </span>
                          <span
                            style={{
                              padding: '3px 8px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: '700',
                              background: badge.background,
                              color: badge.color
                            }}
                          >
                            {badge.label}
                          </span>
                        </div>
                        {leave.reason ? (
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            Reason: {leave.reason}
                          </div>
                        ) : null}
                      </div>

                      {!leave.revoked_at && (
                        <Button
                          variant="secondary"
                          size="small"
                          disabled={submittingLeave}
                          onClick={() => handleRevokeLeave(leave.id)}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default EngineersView;
